import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";

const CartContext = createContext();

const STORAGE_KEY = "ie_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); }
    catch { return []; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

const addItem = useCallback((product, size, qty = 1, color = null) => {
  const sku = `${product.id}__${size || "OS"}__${color || "default"}`;

  // Find image for selected colour
  let selectedImage = product.images?.[0];

  if (color && product.color_images) {
    selectedImage =
      product.color_images[color] ||
      product.color_images[color.toLowerCase()] ||
      selectedImage;
  }

  // Fallback if product stores colours and images as parallel arrays
  if (color && product.colors && product.images) {
    const colorIndex = product.colors.findIndex(
      (c) => c.toLowerCase() === color.toLowerCase()
    );

    if (colorIndex >= 0 && product.images[colorIndex]) {
      selectedImage = product.images[colorIndex];
    }
  }

  setItems((prev) => {
    const existing = prev.find((i) => i.sku === sku);

    if (existing) {
      return prev.map((i) =>
        i.sku === sku
          ? { ...i, qty: i.qty + qty }
          : i
      );
    }

    return [
      ...prev,
      {
        sku,
        id: product.id,
        name: product.name,
        price: product.price,
        image: selectedImage,
        size: size || "OS",
        color: color || null,
        qty,
      },
    ];
  });

  toast.success("Added to bag", {
    description: `${product.name} · ${color || ""} · ${size || "OS"}`,
  });

  setDrawerOpen(true);
}, []);

  const removeItem = useCallback((sku) => {
    setItems((prev) => prev.filter((i) => i.sku !== sku));
  }, []);

  const updateQty = useCallback((sku, qty) => {
    if (qty <= 0) return removeItem(sku);
    setItems((prev) => prev.map((i) => i.sku === sku ? { ...i, qty } : i));
  }, [removeItem]);

  const clear = useCallback(() => setItems([]), []);

  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartContext.Provider value={{
      items, addItem, removeItem, updateQty, clear,
      subtotal, count, drawerOpen, setDrawerOpen,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
